import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { ArrowLeft, Building, Users, Clock, CheckCircle, Star, TrendingUp } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { getBreadcrumbStructuredData, getArticleStructuredData } from '../utils/structuredData';

const SuccessCases = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const breadcrumbData = getBreadcrumbStructuredData([
    { name: 'Home', url: 'https://laimu.mx/' },
    { name: 'Success Cases', url: 'https://laimu.mx/success-cases' }
  ]);

  const seoTitle = language === 'en' 
    ? 'Success Cases - LAIMU Trantor | Customer Stories & Case Studies'
    : 'Casos de Éxito - LAIMU Trantor | Historias de Clientes y Estudios de Caso';
    
  const seoDescription = language === 'en'
    ? 'Discover how companies across automotive, manufacturing, logistics, and printing industries transformed their operations with LAIMU Trantor. Real success stories with measurable results and ROI.'
    : 'Descubre cómo empresas de las industrias automotriz, manufactura, logística e impresión transformaron sus operaciones con LAIMU Trantor. Historias reales de éxito con resultados medibles y ROI.';

  // Create structured data for each case study
  const caseStudyStructuredData = t.successCases.cases.map(caseStudy => 
    getArticleStructuredData({
      title: `${caseStudy.company} Success Story - LAIMU Trantor`,
      description: caseStudy.challenge,
      author: 'LAIMU Team',
      datePublished: '2024-01-15',
      dateModified: '2024-01-15',
      image: caseStudy.imageSrc,
      url: `https://laimu.mx/success-cases#${caseStudy.id}`
    })
  );

  const combinedStructuredData = [breadcrumbData, ...caseStudyStructuredData];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title={seoTitle}
        description={seoDescription}
        url="/success-cases"
        structuredData={combinedStructuredData}
        keywords={language === 'en' 
          ? 'success cases, customer stories, case studies, ERP implementation, CRM success, TMS results, manufacturing efficiency, automotive software, logistics optimization, ROI case studies'
          : 'casos éxito, historias clientes, estudios caso, implementación ERP, éxito CRM, resultados TMS, eficiencia manufactura, software automotriz, optimización logística, estudios ROI'
        }
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ backgroundColor: '#e0f2fe', color: '#115d7b' }}>
              <Star className="w-4 h-4 mr-2" />
              {language === 'en' ? 'Real Success Stories' : 'Historias Reales de Éxito'}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {t.successCases.title}
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {t.successCases.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/demo"
                className="px-8 py-4 text-white rounded-lg transition-colors font-bold text-lg shadow-lg"
                style={{ backgroundColor: '#115d7b' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#0f5269'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#115d7b'}
              >
                {language === 'en' ? 'Start Your Success Story' : 'Inicia Tu Historia de Éxito'}
              </a>
              <a
                href="/#contact"
                className="px-8 py-4 border-2 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-bold text-lg"
                style={{ borderColor: '#115d7b' }}
              >
                {language === 'en' ? 'Contact Expert' : 'Contactar Experto'}
              </a>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="text-3xl font-bold text-gray-900 mb-2">500+</div>
              <div className="text-gray-600 text-sm">
                {language === 'en' ? 'Success Stories' : 'Historias de Éxito'}
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="text-3xl font-bold text-gray-900 mb-2">98%</div>
              <div className="text-gray-600 text-sm">
                {language === 'en' ? 'Client Satisfaction' : 'Satisfacción del Cliente'}
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="text-3xl font-bold text-gray-900 mb-2">45%</div>
              <div className="text-gray-600 text-sm">
                {language === 'en' ? 'Average ROI Increase' : 'Aumento Promedio de ROI'}
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="text-3xl font-bold text-gray-900 mb-2">6</div>
              <div className="text-gray-600 text-sm">
                {language === 'en' ? 'Months to ROI' : 'Meses para ROI'}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 md:px-6 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="space-y-12">
            {t.successCases.cases.map((caseStudy, index) => (
              <article key={caseStudy.id} id={caseStudy.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="relative">
                    <img 
                      src={caseStudy.imageSrc}
                      alt={`${caseStudy.company} success story`}
                      className="w-full h-full object-cover min-h-[400px]"
                      loading={index === 0 ? 'eager' : 'lazy'}
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
                    <div className="absolute bottom-6 left-6 text-white">
                      <div className="flex items-center mb-2">
                        <Building className="w-5 h-5 mr-2" />
                        <span className="text-sm opacity-90">{caseStudy.industry}</span>
                      </div>
                      <h2 className="text-2xl font-bold mb-2">{caseStudy.company}</h2>
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-1" />
                          {caseStudy.employees}
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {caseStudy.timeline}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-8 md:p-12">
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {language === 'en' ? 'Challenge' : 'Desafío'}
                      </h3>
                      <p className="text-gray-600">{caseStudy.challenge}</p>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {language === 'en' ? 'Solution' : 'Solución'}
                      </h3>
                      <p className="text-gray-600 mb-4">{caseStudy.solution}</p>
                      <div className="flex flex-wrap gap-2">
                        {caseStudy.modules.map((module, i) => (
                          <span 
                            key={i}
                            className="px-3 py-1 rounded-full text-sm font-medium"
                            style={{ backgroundColor: '#e0f2fe', color: '#115d7b' }}
                          >
                            {module}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mb-8">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <TrendingUp className="w-5 h-5 mr-2" style={{ color: '#115d7b' }} />
                        {language === 'en' ? 'Results' : 'Resultados'}
                      </h3>
                      <div className="grid grid-cols-1 gap-3">
                        {caseStudy.results.map((result, i) => (
                          <div key={i} className="flex items-start">
                            <CheckCircle className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" style={{ color: '#10b981' }} />
                            <span className="text-gray-700">{result}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-lg">
                      <div className="flex items-center mb-3">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        ))}
                      </div>
                      <blockquote className="text-gray-700 italic mb-4">
                        "{caseStudy.testimonial.quote}"
                      </blockquote>
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-300 rounded-full mr-3"></div>
                        <div>
                          <div className="font-semibold text-gray-900">{caseStudy.testimonial.author}</div>
                          <div className="text-gray-600 text-sm">{caseStudy.testimonial.role}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="bg-white p-8 rounded-xl shadow-md">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {language === 'en' ? 'Ready to Write Your Success Story?' : '¿Listo para Escribir Tu Historia de Éxito?'}
              </h2>
              <p className="text-gray-600 mb-6">
                {language === 'en' 
                  ? 'Join hundreds of companies that have transformed their operations with LAIMU Trantor.'
                  : 'Únete a cientos de empresas que han transformado sus operaciones con LAIMU Trantor.'
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/demo"
                  className="px-6 py-3 text-white rounded-md transition-colors font-medium"
                  style={{ backgroundColor: '#115d7b' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#0f5269'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#115d7b'}
                >
                  {language === 'en' ? 'Request Demo' : 'Solicitar Demo'}
                </a>
                <a
                  href="/#contact"
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
                >
                  {language === 'en' ? 'Contact Sales' : 'Contactar Ventas'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default SuccessCases;